import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Divider,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import DashboardLayout from "../../layout/DashboardLayout";
import { getPccRequest, requestPcc, queryPcc, getKycStatus } from "../../api/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { brand } from "../../theme";

const STEPS = ["Request Submitted", "Under Helpdesk Review", "PCC Assigned"];

function businessDaysSince(date) {
  if (!date) return 0;
  const d = new Date(date);
  const now = new Date();
  let count = 0;
  while (d < now) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) count++;
  }
  return count;
}

function hoursSince(date) {
  if (!date) return Infinity;
  return (Date.now() - new Date(date)) / (1000 * 60 * 60);
}

export default function PCCRequestPage() {
  const navigate = useNavigate();
  const [pccRequest, setPccRequest] = useState(null);
  const [kycSubmitted, setKycSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [querying, setQuerying] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pccRes, kycRes] = await Promise.all([getPccRequest(), getKycStatus()]);
        if (pccRes.data?.success) setPccRequest(pccRes.data.data);
        else toast.warn(pccRes.data?.message || "Unable to fetch PCC request");
        setKycSubmitted(kycRes.data?.kycStatus === "Submitted");
      } catch {
        toast.error("Unable to load page data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handlePccRequest = async () => {
    try {
      setSubmitting(true);
      const res = await requestPcc();
      if (res.data?.success) {
        setPccRequest(res.data.data);
        toast.success("PCC request submitted successfully");
      } else {
        toast.warn(res.data?.message || "Unable to submit PCC request");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuery = async () => {
    try {
      setQuerying(true);
      const res = await queryPcc();
      if (res.data?.success) {
        setPccRequest((prev) => ({ ...prev, lastQueriedAt: res.data.data.lastQueriedAt }));
        toast.success("Your query has been sent to the Helpdesk team.");
      } else {
        toast.warn(res.data?.message || "Unable to send query");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send query");
    } finally {
      setQuerying(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved": return "success";
      case "pending": return "warning";
      case "rejected": return "error";
      default: return "default";
    }
  };

  const getActiveStep = (status) => {
    if (status === "Approved") return 2;
    if (status === "Pending") return 1;
    return 0;
  };

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleString("en-GB", { dateStyle: "long", timeStyle: "short" })
      : "";

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );

  const status = pccRequest?.status || "Not Submitted";
  const canSubmit = kycSubmitted && ["Not Submitted", "Rejected"].includes(status);
  const isPending = status === "Pending";
  const daysPending = businessDaysSince(pccRequest?.requestedAt);
  const recentlyQueried = isPending && hoursSince(pccRequest?.lastQueriedAt) < 24;
  const canQuery = isPending && daysPending >= 2 && !recentlyQueried;

  return (
    <DashboardLayout>
      <Box p={3} maxWidth={720}>
        {/* Page header */}
        <Box mb={3}>
          <Typography variant="h5" fontWeight={700} color="#0f172a">
            Pseudo City Code (PCC)
          </Typography>
          <Typography variant="body2" color="#64748b" mt={0.5}>
            Submit and track your agency's PCC request with the Helpdesk.
          </Typography>
        </Box>

        {/* KYC gate */}
        {!kycSubmitted && (
          <Alert
            severity="warning"
            icon={<LockOutlinedIcon />}
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate("/agency/kyc-form")}>
                Complete KYC
              </Button>
            }
          >
            You must complete your KYC verification before you can request a PCC.
          </Alert>
        )}

        {/* Status bar + submit button */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="#0f172a">
                Request Status
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Chip label={status} color={getStatusColor(status)} size="small" />
                {isPending && (
                  <Typography variant="caption" color="#64748b">
                    {daysPending} business day{daysPending !== 1 ? "s" : ""} pending
                  </Typography>
                )}
              </Box>
            </Box>
            {canSubmit && (
              <Button
                variant="contained"
                onClick={handlePccRequest}
                disabled={submitting}
                startIcon={<FlightTakeoffIcon />}
              >
                {submitting
                  ? "Submitting…"
                  : status === "Rejected"
                  ? "Resubmit PCC Request"
                  : "Request PCC"}
              </Button>
            )}
          </Box>
        </Paper>

        {/* Rejection notice */}
        {status === "Rejected" && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={600} mb={0.5}>
              Your PCC request was not approved.
            </Typography>
            <Typography variant="body2">
              {pccRequest?.notes ? `Reason: ${pccRequest.notes}` : "No reason was provided."}
            </Typography>
            <Typography variant="body2" mt={0.5}>
              Use the button above to submit a new request.
            </Typography>
          </Alert>
        )}

        {/* Progress stepper */}
        {status !== "Not Submitted" && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography
              variant="caption"
              color="#94a3b8"
              textTransform="uppercase"
              letterSpacing={1}
              fontWeight={600}
              display="block"
              mb={2.5}
            >
              Progress
            </Typography>
            <Stepper activeStep={getActiveStep(status)} alternativeLabel>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>
        )}

        {/* Request details */}
        {status !== "Not Submitted" && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography
              variant="caption"
              color="#94a3b8"
              textTransform="uppercase"
              letterSpacing={1}
              fontWeight={600}
              display="block"
              mb={2}
            >
              Request Details
            </Typography>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <Box>
                <Typography variant="caption" color="#94a3b8">Submitted On</Typography>
                <Typography variant="body2" fontWeight={500}>{formatDate(pccRequest?.requestedAt)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="#94a3b8">Processed By</Typography>
                <Typography variant="body2" fontWeight={500}>{pccRequest?.approvedBy}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="#94a3b8">Processed On</Typography>
                <Typography variant="body2" fontWeight={500}>{formatDate(pccRequest?.processedAt)}</Typography>
              </Box>
              {pccRequest?.lastQueriedAt && (
                <Box>
                  <Typography variant="caption" color="#94a3b8">Last Queried</Typography>
                  <Typography variant="body2" fontWeight={500}>{formatDate(pccRequest.lastQueriedAt)}</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        )}

        {/* Query card — shown while Pending */}
        {isPending && (
          <Paper
            sx={{
              p: 3,
              mb: 3,
              border: canQuery ? "1px solid #fde68a" : "1px solid #e2e8f0",
              bgcolor: canQuery ? "#fffbeb" : "#f8fafc",
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <HelpOutlineIcon sx={{ color: canQuery ? "#d97706" : "#94a3b8", mt: 0.3 }} />
              <Box flex={1}>
                <Typography variant="subtitle2" fontWeight={600} color={canQuery ? "#92400e" : "#475569"}>
                  {canQuery ? "Taking longer than expected?" : "Need to follow up?"}
                </Typography>
                <Typography variant="body2" color="#64748b" mt={0.5}>
                  {recentlyQueried
                    ? "Your query has been sent. The Helpdesk team has been notified and will update your request shortly."
                    : canQuery
                    ? `Your request has been pending for ${daysPending} business days. Send a query to the Helpdesk to request an update.`
                    : daysPending < 2
                    ? "PCC requests are typically processed within 5 business days. The query button becomes available after 2 business days."
                    : "A query was sent recently. You can send another after 24 hours."}
                </Typography>

                {recentlyQueried && (
                  <Alert
                    severity="success"
                    icon={<CheckCircleOutlineIcon fontSize="small" />}
                    sx={{ mt: 1.5, py: 0.5 }}
                  >
                    Query sent — the Helpdesk team has been notified.
                  </Alert>
                )}

                {canQuery && (
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    onClick={handleQuery}
                    disabled={querying}
                    startIcon={<HelpOutlineIcon />}
                    sx={{ mt: 1.5 }}
                  >
                    {querying ? "Sending query…" : "Query Helpdesk"}
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        )}

        {/* Approved — PCC details */}
        {status === "Approved" && (
          <Paper sx={{ p: 3, border: "1px solid #bbf7d0", bgcolor: "#f0fdf4" }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CheckCircleOutlineIcon sx={{ color: "#16a34a" }} />
              <Typography variant="subtitle1" fontWeight={700} color="#14532d">
                Your PCC is Active
              </Typography>
            </Box>
            <Divider sx={{ mb: 2.5 }} />
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <Box>
                <Typography variant="caption" color="#94a3b8">Pseudo City Code</Typography>
                <Typography variant="h6" fontWeight={700} color={brand.terracotta}>
                  {pccRequest?.pccCode}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="#94a3b8">EPR</Typography>
                <Typography variant="body2" fontWeight={500}>{pccRequest?.epr}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="#94a3b8">Password</Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <LockOutlinedIcon sx={{ fontSize: "0.9rem", color: "#64748b" }} />
                  <Typography variant="body2" fontWeight={500} fontFamily="monospace">
                    {pccRequest?.password}
                  </Typography>
                </Box>
              </Box>
              {pccRequest?.notes && (
                <Box sx={{ gridColumn: "span 2" }}>
                  <Typography variant="caption" color="#94a3b8">Notes</Typography>
                  <Typography variant="body2">{pccRequest.notes}</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        )}
      </Box>
    </DashboardLayout>
  );
}
